/** Helpers shared across the guided "My Life Story" (ethical will) experience. */

import {fromJS, List} from 'immutable'

import {LIFE_STORY_CHAPTERS, findChapter, newEvergreenPromptId} from './lifeStoryContent'
import {fileExtension} from '../../../../../shared_components/forms/v2/lib/tools'
import {storyCountLabel} from '../../../../../lib/lifeStoryDisplay'

export {storyCountLabel}

export const ETHICAL_WILL_ITEM_ID = 'ca0bd9df-7ed8-4110-83cb-af5a12954fd7'
export const ETHICAL_WILL_ITEM_SLUG = 'my-story'
export const LIFE_AND_LEGACY_CATEGORY_ID = '81d7865a-37ef-4830-a3f1-ad02854cc4bf'

export const DEFAULT_LIFE_STORY_TITLE = 'My Life Story'

export const LIFE_STORY_FLOW_PATH = '/cm/life-story'

export const LIFE_STORY_DISMISS_FLAG = 'has_dismissed_life_story_module'

export const LIFE_AND_LEGACY_CATEGORY_SLUG = 'life-and-legacy'

export const LIFE_AND_LEGACY_CATEGORY_PATH = `/cm/category/${LIFE_AND_LEGACY_CATEGORY_SLUG}`

// One answer tops out at 25,000 characters (TK-2983 AC 3.9). That's sized
// above what a maxed-out 10-minute recording can transcribe (~13k characters
// at a fast 220 wpm), so dictation only reaches it when appending to an
// already long written draft.
export const MAX_STORY_LENGTH = 25000

/** e.g. "The Life of Andrea"; falls back to the generic title without a name. */
export const defaultLifeStoryTitle = firstName => (firstName ? `The Life of ${firstName}` : DEFAULT_LIFE_STORY_TITLE)

/**
 * The ethical will `data` payload persisted to the API. Keys are dasherized so
 * that what the client writes matches what the decrypting reader returns.
 *
 * {
 *   title: 'The Life of Andrea',
 *   stories: [{
 *     'prompt-id', 'chapter-key', 'chapter-name', 'question', 'text', 'skipped',
 *     recording: {'upload-id', duration, filename},
 *     photos: [{'upload-id', filename}]  // up to three, in display order (TK-3108)
 *   }]
 * }
 */

// Leaves room within each chapter's range for prompts added to the config later.
const CHAPTER_ORDER_SPAN = 100

const {configOrder, chapterOrder} = (() => {
  const promptPositions = {}
  const chapterPositions = {}

  LIFE_STORY_CHAPTERS.forEach((chapter, chapterIndex) => {
    chapterPositions[chapter.key] = chapterIndex
    chapter.prompts.forEach((prompt, promptIndex) => {
      promptPositions[prompt.id] = (chapterIndex * CHAPTER_ORDER_SPAN) + promptIndex
    })
  })

  return {configOrder: promptPositions, chapterOrder: chapterPositions}
})()

// Evergreen ("Is there anything else…") stories aren't in the prompt
// configuration: they sort to the end of their own chapter, kept in the order
// they were told (Array#sort is stable).
const storyOrder = story => {
  const configured = configOrder[story['prompt-id']]

  if (typeof configured !== 'undefined') return configured

  const chapterIndex = chapterOrder[story['chapter-key']]

  if (typeof chapterIndex !== 'undefined') return (chapterIndex * CHAPTER_ORDER_SPAN) + CHAPTER_ORDER_SPAN - 1

  return Number.MAX_SAFE_INTEGER
}

/** Keeps stories in chapter/prompt configuration order so the PDF chapters render in order. */
export const sortStories = stories =>
  [...stories].sort((storyA, storyB) => storyOrder(storyA) - storyOrder(storyB))

/** Adds or replaces the story for a prompt and returns a payload ready to persist. */
export const upsertStory = ({stories, story}) => sortStories([
  ...stories.filter(existingStory => existingStory['prompt-id'] !== story['prompt-id']),
  story
])

export const removeStory = ({stories, promptId}) =>
  stories.filter(existingStory => existingStory['prompt-id'] !== promptId)

// A story carries up to three recordings (TK-3204 "Record this version"):
// `recording` is what the family hears and the only one published;
// `draft-recording` is a new take waiting for the writer to choose between
// it and the current one; `replaced-recording` is the previous take, kept
// only while the Undo on "Use new recording" is offered.
export const buildStory = ({chapter, prompt, text, recording, draftRecording, replacedRecording, photos, skipped = false}) => ({
  'prompt-id': prompt.id,
  'chapter-key': chapter.key,
  'chapter-name': chapter.name,
  question: prompt.question,
  text: text || '',
  skipped,
  ...recording ? {recording} : {},
  ...draftRecording ? {'draft-recording': draftRecording} : {},
  ...replacedRecording ? {'replaced-recording': replacedRecording} : {},
  ...photos && photos.length > 0 ? {photos} : {}
})

/** Whether a story carries anything of the writer's: words, a recording or a photo. */
export const storyHasContent = story =>
  (story.text || '').trim().length > 0 || Boolean(story.recording) || Boolean(story.photos && story.photos.length > 0)

// A recording alone counts as an answer: dictation isn't supported everywhere,
// so a story can carry a voice recording with no transcribed text. So does a
// photo on its own (TK-3108 AC 4.3).
export const answeredStories = stories =>
  stories.filter(story => !story.skipped && storyHasContent(story))

/** Whether any question in the chapter has a saved answer. */
export const chapterHasAnswers = ({chapterKey, stories}) =>
  answeredStories(stories).some(story => story['chapter-key'] === chapterKey)

/** Whether a prompt has a saved answer; skipped and never-attempted prompts alike read as unanswered. */
export const promptIsAnswered = ({promptId, stories}) =>
  answeredStories(stories).some(story => story['prompt-id'] === promptId)

/**
 * The prompt to offer next in a chapter: the first configured prompt without
 * a story, scanning from just after `afterPromptId` and wrapping around. The
 * `afterPromptId` prompt itself is excluded — its save may not be reflected
 * in `stories` yet, and re-offering the question just finished would leave
 * the user stuck on it.
 *
 * With `revisitSkipped` (entering a chapter fresh, e.g. Review's "Add a
 * story"), previously skipped questions are offered again once the untouched
 * ones run out (TK-2983 AC 6.4). Mid-flow advancement leaves it off so a
 * question skipped moments ago isn't asked again in the same breath.
 *
 * Chapters never run dry — there's no finish line: once every configured
 * prompt is used up this mints a fresh evergreen "Is there anything else…"
 * prompt, answerable again and again.
 */
export const nextPromptIdInChapter = ({chapterKey, stories, afterPromptId = null, revisitSkipped = false}) => {
  const chapter = findChapter(chapterKey)
  const storyForPrompt = promptId => stories.find(story => story['prompt-id'] === promptId)
  const startIndex = afterPromptId ? chapter.prompts.findIndex(prompt => prompt.id === afterPromptId) + 1 : 0
  const remaining = [...chapter.prompts.slice(startIndex), ...chapter.prompts.slice(0, startIndex)]
    .filter(prompt => prompt.id !== afterPromptId)
  const untouched = remaining.find(prompt => !storyForPrompt(prompt.id))

  if (untouched) return untouched.id

  if (revisitSkipped) {
    const skipped = remaining.find(prompt => (storyForPrompt(prompt.id) || {}).skipped)

    if (skipped) return skipped.id
  }

  return newEvergreenPromptId(chapterKey)
}

/**
 * Strips the vault response group of a life story that's inside its
 * undo-after-delete window from the given item responses. The DELETE hasn't
 * been sent yet, so the vault still returns the group — but as far as the
 * user is concerned the story is already gone (TK-2983 AC 6.8), and only the
 * undo bar can bring it back. With its only group stripped, the Ethical Will
 * item falls back to its "not started" presentation.
 */
export const withoutPendingLifeStoryGroup = ({itemResponses, pendingDeletion}) => {
  const firstGroupId = pendingDeletion && pendingDeletion.firstGroupId

  if (!firstGroupId) return itemResponses

  return itemResponses.map(itemResponse => (
    itemResponse.get('item-id') === ETHICAL_WILL_ITEM_ID ?
      itemResponse.update('response-groups', List(), groups => groups.filterNot(group => group.get('id') === firstGroupId)) :
      itemResponse
  ))
}

export const storiesByChapter = stories => {
  const grouped = []

  LIFE_STORY_CHAPTERS.forEach(chapter => {
    const chapterStories = answeredStories(stories).filter(story => story['chapter-key'] === chapter.key)

    if (chapterStories.length > 0)
      grouped.push({chapter, stories: chapterStories})
  })

  return grouped
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

const numberWord = number => NUMBER_WORDS[number] || `${number}`

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1)

/** Story-module line shown to readers who aren't the author (deputies). */
export const storySummaryForViewer = ({stories, ownerFirstName}) => {
  const hasRecordings = answeredStories(stories).some(story => story.recording)
  const whose = ownerFirstName ? `${ownerFirstName}\u2019s` : 'their'

  return `Chapters of ${whose} life, in their own words${hasRecordings ? ' \u2014 and, in places, their own voice' : ''}.`
}

/** Summary line for the item modal, mirroring the mock copy. */
export const storySummary = stories => {
  const answered = answeredStories(stories)
  const recordedCount = answered.filter(story => story.recording).length
  const base = `${capitalize(numberWord(answered.length))} ${answered.length === 1 ? 'story' : 'stories'} set down`
  const closing = 'Whenever a memory finds you, pick up right where you left off.'

  if (recordedCount === 0) return `${base}. ${closing}`
  if (answered.length === 1 || recordedCount === answered.length) return `${base} — in your own voice. ${closing}`

  return `${base} — ${numberWord(recordedCount)} in your own voice. ${closing}`
}

export const lifeStoryPdfFilename = title => {
  const base = (title || DEFAULT_LIFE_STORY_TITLE)
    .trim()
    .replace(/[^0-9A-Za-z]+/g, '_')
    .replace(/^_+|_+$/g, '')

  return `${base || 'life_story'}.pdf`
}

export const attachmentsWithPdfFirst = files => {
  const isPdf = file => fileExtension(file.get('name') || '') === 'pdf'

  return files.filter(isPdf).concat(files.filterNot(isPdf))
}

/** Filename given to a voice recording upload, derived from the question. */
export const recordingFilename = ({question, extension}) => {
  const base = (question || '')
    .replace(/[^0-9A-Za-z\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 5)
    .join('_')

  return `${base || 'life_story_recording'}.${extension}`
}

export const formatDuration = seconds => {
  const safeSeconds = Math.max(0, Math.floor(seconds || 0))

  return `${Math.floor(safeSeconds / 60)}:${`0${safeSeconds % 60}`.slice(-2)}`
}

/** Cleans raw redux-json-api `ethical-wills` resources into a List of attribute Maps (with id). */
export const cleanEthicalWills = rawEthicalWills => {
  const data = rawEthicalWills && rawEthicalWills.data ? rawEthicalWills.data : []

  return List(data.map(resource => fromJS(resource.attributes).set('id', resource.id)))
}

/** Finds the ethical will whose vault response group matches the given first group id. */
export const ethicalWillByFirstGroupId = ({ethicalWills, firstGroupId}) =>
  ethicalWills.find(ethicalWill => ethicalWill.get('first-group-id') && ethicalWill.get('first-group-id') === firstGroupId)

/** The most recently updated ethical will that hasn't been saved to the vault yet, if any. */
export const latestDraft = ethicalWills =>
  ethicalWills
    .filter(ethicalWill => !ethicalWill.get('saved-to-vault-at'))
    .sortBy(ethicalWill => ethicalWill.get('updated-at'))
    .last() || null

/** The most recently updated ethical will, draft or published, if any. */
export const latestEthicalWill = ethicalWills =>
  ethicalWills.sortBy(ethicalWill => ethicalWill.get('updated-at')).last() || null

export const lifeStoryContinuePath = ethicalWillId => `${LIFE_STORY_FLOW_PATH}/${ethicalWillId}`

/**
 * The guided experience itself is premium. Freemium funeral home clients still
 * see the module (it's part of what they're being sold), but entering the flow
 * is gated the same way as any other premium item.
 */
export const lifeStoryLockedForUser = userConfig => !userConfig.get('premium')

/**
 * Full-page story view. Owners read their own story at the plain path; the
 * preview variant (deputies, or the owner previewing their plan) carries the
 * everplan id so the screen can fetch and exit within that everplan's context.
 */
export const lifeStoryStoryPath = ({ethicalWillId, everplanId = null}) => (
  everplanId ?
    `/cm/everplan-preview/${everplanId}/life-story/${ethicalWillId}/story` :
    `${LIFE_STORY_FLOW_PATH}/${ethicalWillId}/story`
)

/**
 * Where the story view's back nav lands within an everplan preview: the Life
 * Story item modal the reader entered from, reopened on that everplan
 * (TK-3059). Without the story's vault group id (stale link, discarded
 * story) there is no modal to open, so it falls back to the preview itself.
 */
export const everplanPreviewStoryExitPath = ({everplanId, firstGroupId = null}) => (
  firstGroupId ?
    `/cm/everplan-preview/${everplanId}/category/${LIFE_AND_LEGACY_CATEGORY_SLUG}/item/${ETHICAL_WILL_ITEM_SLUG}/view/${firstGroupId}` :
    `/cm/everplan-preview/${everplanId}`
)
